"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import type { CandidatePersonalizationController } from "./useCandidatePersonalization";
import type {
  PersonalizationSheetStep,
  ResumeReviewForm,
} from "../personalization.types";
import {
  EMPTY_REVIEW_FORM,
  toEditableProfile,
  toReviewForm,
} from "../personalization.utils";
import { PersonalizationApiError } from "../services/personalizationClient";

type UseResumePersonalizationSheetInput = {
  onClose: () => void;
  controller: CandidatePersonalizationController;
};

export function useResumePersonalizationSheet({
  onClose,
  controller,
}: UseResumePersonalizationSheetInput) {
  const t = useTranslations("profile.resumePersonalization");
  const [step, setStep] = useState<PersonalizationSheetStep>(() =>
    controller.isSessionLoading
      ? "parsing"
      : controller.isPersonalized
        ? "manage"
        : "upload",
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reviewForm, setReviewForm] =
    useState<ResumeReviewForm>(EMPTY_REVIEW_FORM);
  const [signInEmail, setSignInEmail] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const chooseFile = (file: File | null) => {
    setSelectedFile(file);
    setLocalError(null);
  };

  const upload = async () => {
    if (!selectedFile) {
      setLocalError(t("errors.chooseResume"));
      return;
    }
    setStep("parsing");
    setLocalError(null);
    try {
      const result = await controller.uploadResume(selectedFile);
      if (result.editableProfile) {
        setReviewForm(toReviewForm(result.editableProfile));
      }
      setStep("review");
    } catch {
      setStep("upload");
    }
  };

  const review = async () => {
    const profile = toEditableProfile(reviewForm);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    if (
      profile.titles.length === 0 &&
      profile.skills.length === 0 &&
      profile.keywords.length === 0
    ) {
      setLocalError(t("errors.missingPreferences"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(profile.email);
    setStep("otp");
    try {
      const verificationRequired =
        await controller.requestProfileVerification(profile);
      if (!verificationRequired) onClose();
    } catch {
      setStep("review");
    }
  };

  const signIn = async () => {
    const email = signInEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(email);
    setStep("otp");
    try {
      const verificationRequired = await controller.requestSignIn(email);
      if (!verificationRequired) onClose();
    } catch (error) {
      if (
        error instanceof PersonalizationApiError &&
        error.code === "CANDIDATE_PROFILE_NOT_FOUND"
      ) {
        setLocalError(t("errors.profileNotFound"));
        setStep("upload");
        return;
      }
      setStep("sign_in");
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setLocalError(t("errors.invalidCode"));
      return;
    }
    setLocalError(null);
    try {
      const session = await controller.verifyCode(verificationEmail, code);
      if (session.hasResume) onClose();
      else setStep("upload");
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const resend = async () => {
    setLocalError(null);
    try {
      if (controller.verificationMode === "login") {
        await controller.requestSignIn(verificationEmail);
      } else {
        await controller.requestProfileVerification(
          toEditableProfile(reviewForm),
        );
      }
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const goBack = () => {
    setStep(
      step === "otp"
        ? controller.verificationMode === "login"
          ? "sign_in"
          : "review"
        : "upload",
    );
  };

  const showUpload = () => {
    setLocalError(null);
    setSelectedFile(null);
    setStep("upload");
  };

  const logout = async () => {
    try {
      await controller.logout();
      setStep("upload");
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  return {
    step,
    selectedFile,
    reviewForm,
    signInEmail,
    verificationEmail,
    code,
    error: localError || controller.error,
    showBack: step === "review" || step === "sign_in" || step === "otp",
    chooseFile,
    setReviewForm,
    setSignInEmail,
    setCode,
    setStep,
    upload,
    review,
    signIn,
    verify,
    resend,
    goBack,
    showUpload,
    logout,
  };
}
